import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Search, Edit as EditIcon, Trash2 as DeleteIcon, Plus as PlusIcon, LayoutPanelLeft as DepartmentsLogoIcon } from 'lucide-react';
import LoaderProgress from '../components/ui/loader-progress';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function AppDepartments() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState('');

  const fetchData = async () => {
    try {
      const res = await api.get('/app-departments');
      setList(res.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Errore nel caricamento dei dipartimenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentId(item.id);
      setName(item.name);
    } else {
      setCurrentId(null);
      setName('');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    try {
      if (currentId) {
        await api.put(`/app-departments/${currentId}`, { name });
        toast.success('Dipartimento aggiornato con successo');
      } else {
        await api.post('/app-departments', { name });
        toast.success('Dipartimento creato con successo');
      }
      handleCloseDialog();
      setLoading(true);
      fetchData();
    } catch (error) {
      console.error('Error saving department:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio del dipartimento');
    }
  };

  const openDeleteDialog = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/app-departments/${deleteTarget.id}`);
      toast.success('Dipartimento eliminato con successo');
      closeDeleteDialog();
      setLoading(true);
      fetchData();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione del dipartimento');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredList = list
    .filter((item) => {
      if (!filterName) return true;
      const search = filterName.toLowerCase();
      return item.name.toLowerCase().includes(search);
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'id') {
        return isAsc ? a.id - b.id : b.id - a.id;
      }
      if (orderBy === 'user_count') {
        const valA = a.user_count || 0;
        const valB = b.user_count || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      if (orderBy === 'user_percentage') {
        const valA = a.user_percentage || 0;
        const valB = b.user_percentage || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
      const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DepartmentsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Dipartimenti (App principale)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione dei dipartimenti aziendali per ruoli e assegnazioni
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlusIcon size={18} />}
          onClick={() => handleOpenDialog()}
        >
          Nuovo Dipartimento
        </Button>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca per nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'user_count'}
                    direction={orderBy === 'user_count' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_count')}
                  >
                    Totale Risorse Umane
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'user_percentage'}
                    direction={orderBy === 'user_percentage' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_percentage')}
                  >
                    % sul totale
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{item.name}</TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            border: '1px solid rgba(245, 158, 11, 0.35)',
                            backgroundColor: 'rgba(245, 158, 11, 0.04)',
                            minWidth: 56,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: '#F59E0B',
                              letterSpacing: 0.2,
                            }}
                          >
                            {typeof item.user_count === 'number' ? item.user_count : 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const rawPercent = Number(item.user_percentage);
                          const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                          const clampedPercent = Math.max(0, Math.min(100, safePercent));
                          return (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={36}
                                  thickness={6}
                                  sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                />
                                <CircularProgress
                                  variant="determinate"
                                  value={clampedPercent}
                                  size={36}
                                  thickness={6}
                                  sx={{ position: 'absolute', left: 0 }}
                                  color="warning"
                                />
                                <Box
                                  sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                    {safePercent.toFixed(0)}%
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(item)} color="primary">
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton size="small" onClick={() => openDeleteDialog(item)} color="error">
                          <DeleteIcon size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{currentId ? 'Modifica Dipartimento' : 'Nuovo Dipartimento'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile. Stai per eliminare il dipartimento:
          </DialogContentText>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
